import { Icon } from "@opendash/icons";
import { Button, Space, Table, Tag, Tooltip, Typography } from "antd";
import { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { SIV_Package } from "../../../parsegenerated";

type Props = {
  packages: SIV_Package[];
  loading?: boolean;
  onViewDetails?: (pkg: SIV_Package) => void;
};

const getStatusColor = (status?: string): string => {
  switch (status?.toLowerCase()) {
    case "completed":
    case "fertig":
      return "green";
    case "in progress":
    case "in bearbeitung":
      return "blue";
    case "pending":
    case "offen":
      return "orange";
    default:
      return "default";
  }
};

export const PackagesTable = observer((props: Props) => {
  const { packages, loading, onViewDetails } = props;

  const columns: ColumnType<SIV_Package>[] = [
    {
      title: "Nr.",
      dataIndex: "no",
      key: "no",
      sorter: (a, b) => a.no.localeCompare(b.no),
    },
    {
      title: "Verpackungsdatum",
      dataIndex: "packagingDate",
      key: "packagingDate",
      render: (date: Date) => (date ? dayjs(date).format("DD.MM.YYYY") : "-"),
      sorter: (a, b) => {
        if (!a.packagingDate || !b.packagingDate) return 0;
        return (
          dayjs(a.packagingDate).valueOf() - dayjs(b.packagingDate).valueOf()
        );
      },
    },
    {
      title: "Verpackungsart",
      dataIndex: "packagingType",
      key: "packagingType",
      render: (val: string) =>
        val ? (
          <Tag color="purple">
            <Icon icon="fa:box" /> {val}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Abmessungen (L×B×H)",
      key: "dimensions",
      render: (_, record) => {
        if (
          record.packageLength ||
          record.packageWidth ||
          record.packageHeight
        ) {
          return (
            <Typography.Text>
              {record.packageLength || "-"} × {record.packageWidth || "-"} ×{" "}
              {record.packageHeight || "-"} mm
            </Typography.Text>
          );
        }
        return "-";
      },
    },
    {
      title: "Gewicht (Netto/Brutto)",
      key: "weight",
      render: (_, record) => {
        if (record.netWeight || record.grossWeight) {
          return (
            <Typography.Text>
              {record.netWeight ?? "-"} / {record.grossWeight ?? "-"} kg
            </Typography.Text>
          );
        }
        return "-";
      },
    },
    {
      title: "Status",
      dataIndex: "statusProcess",
      key: "status",
      render: (val: string) =>
        val ? <Tag color={getStatusColor(val)}>{val}</Tag> : "-",
    },
    {
      title: "Kurzbeschreibung",
      dataIndex: "shortDescription",
      key: "shortDescription",
      ellipsis: true,
      render: (val: string) => val || "-",
    },
    {
      title: "Lieferschein",
      key: "deliveryNote",
      render: (_, record) =>
        record.deliveryNote ? (
          <Tag color="green">{record.deliveryNote.no}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Aktionen",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space.Compact>
          {onViewDetails && (
            <Tooltip title="Details anzeigen">
              <Button
                size="small"
                icon={<Icon icon="fa:list" />}
                onClick={() => onViewDetails(record)}
              />
            </Tooltip>
          )}
        </Space.Compact>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={packages}
      loading={loading}
      rowKey={(record) => record.id!}
      size="small"
      pagination={{ pageSize: 10 }}
    />
  );
});
